import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { HiLightBulb, HiCpuChip, HiScissors, HiCheckCircle, HiArrowUpRight } from 'react-icons/hi2';

export default function Divisions() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section className="relative bg-charcoal text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-transparent" />
        <div className="absolute -top-20 right-0 w-80 h-80 bg-accent/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">What We Do</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Business <span className="text-gradient-gold">Divisions</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Engineering-driven division units powering commercial development, solar sustainability, precise tooling, and distribution safety.
          </p>
        </div>
      </section>

      {/* Division Navigation Tabs */}
      <div className="bg-white border-b border-border sticky top-14 z-30 shadow-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-5 overflow-x-auto py-3.5">
          {[
            { href: '#solar', icon: <HiLightBulb className="w-4 h-4 text-accent" />, label: 'Solar Solutions' },
            { href: '#panels', icon: <HiCpuChip className="w-4 h-4 text-primary" />, label: 'Electrical Panels' },
            { href: '#tools', icon: <HiScissors className="w-4 h-4 text-accent" />, label: 'Cutting Tools' },
            { href: '/generator-sales', icon: <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>, label: 'Generator Solutions', isLink: true },
          ].map((item) => (
            item.isLink
              ? <Link key={item.label} to={item.href} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-body hover:text-primary whitespace-nowrap transition-colors">
                  {item.icon} {item.label}
                </Link>
              : <a key={item.label} href={item.href} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-body hover:text-primary whitespace-nowrap transition-colors">
                  {item.icon} {item.label}
                </a>
          ))}
        </div>
      </div>

      {/* Division 1: Solar Solutions */}
      <section id="solar" className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="icon-box card-icon mb-6">
              <HiLightBulb className="w-6 h-6" />
            </div>
            <span className="eyebrow">Renewable Power</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-6">
              Solar EPC Projects for Commercial & Industrial Needs
            </h2>
            <p className="text-sm text-body leading-relaxed mb-8">
              We provide full-spectrum Engineering, Procurement, and Commissioning (EPC) solar deployment services. Our teams draft architectural blueprints, manage land load dynamics, interface with local state electricity boards, and handle grid net-metering approvals.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { title: 'On-Grid Systems', desc: 'Directly synchronized with state grids. Ideal for peak utility bill offset.' },
                { title: 'Hybrid & Off-Grid Setup', desc: 'Combines battery backup cycles with diesel generator synchronization.' },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-heading uppercase mb-1">{f.title}</h4>
                    <p className="text-xs text-body leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-premium relative overflow-hidden group hover:border-primary-200 transition-all duration-300">
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-accent/8 rounded-full blur-2xl" />
            <h3 className="font-display font-bold text-xs text-accent uppercase tracking-widest mb-7 relative z-10">Solar Installation Specs</h3>
            <div className="space-y-4 text-xs relative z-10">
              {[
                { k: 'Available Sizes', v: '10 kWp to 2 MWp+' },
                { k: 'Panel Types', v: 'Mono PERC / Bifacial' },
                { k: 'Inverter Tech', v: 'String / Micro inverters' },
                { k: 'Guarantees', v: '25 Yr Performance Warranty' },
              ].map((row, i) => (
                <div key={i} className={`flex justify-between py-3 ${i < 3 ? 'border-b border-white/10' : ''}`}>
                  <span className="text-white/60 font-medium">{row.k}</span>
                  <span className="font-bold text-white">{row.v}</span>
                </div>
              ))}
            </div>
            <Link to="/contact#enquiry" className="btn-accent w-full justify-center mt-8 py-3 rounded-xl text-xs relative z-10">
              Get Solar Feasibility Audit
              <HiArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Division 2: Electrical Panels */}
      <section id="panels" className="bg-light-section text-heading py-24 relative overflow-hidden border-t border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-light/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-light/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start relative z-10">

          <div className="lg:col-span-5 bg-white border border-border shadow-card rounded-2xl p-8">
            <h3 className="font-display font-bold text-xs text-accent uppercase tracking-widest mb-7">Panel Product Range</h3>
            <ul className="space-y-4">
              {[
                { name: 'LT Distribution Panel Boards', spec: 'Up to 6300A' },
                { name: 'AMF / Synchronizing Panels', spec: 'Auto Changeover' },
                { name: 'APFC Capacitor Panels', spec: 'Power Factor Correction' },
                { name: 'Motor Control Centers (MCC)', spec: 'Smart Starters' },
              ].map((p, i) => (
                <li key={i} className="flex items-center justify-between text-xs border-b border-primary-100 pb-4 last:border-0 last:pb-0">
                  <span className="text-body font-medium">{p.name}</span>
                  <span className="font-bold text-heading">{p.spec}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="icon-box card-icon mb-6">
              <HiCpuChip className="w-6 h-6" />
            </div>
            <span className="eyebrow">Switchgear Engineering</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-6">LT & HT Distribution Panel Boards</h2>
            <p className="text-sm text-body leading-relaxed mb-8">
              Our panels are custom designed in compliance with CPRI approvals and IEC regulations. We specialize in Auto Mains Failure (AMF) panels that sync multiple diesel generator sets automatically, adjusting voltage and loads seamlessly to guarantee zero disruption.
            </p>
            <div className="flex flex-wrap gap-3">
              {['CPRI Approved', 'IP55/IP65 Enclosures', 'Siemens/ABB Components'].map((tag, i) => (
                <span key={i} className="bg-primary-50 border border-primary-100 text-primary text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-lg">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Division 3: Cutting Tools */}
      <section id="tools" className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="icon-box card-icon mb-6">
              <HiScissors className="w-6 h-6" />
            </div>
            <span className="eyebrow">Industrial Distribution</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-6">
              Precision Machining & Solid Carbide Cutting Tools
            </h2>
            <p className="text-sm text-body leading-relaxed mb-8">
              Green Gusto acts as the premium distribution partner for major tooling OEMs. We supply machine shops, heavy engineering units, and automotive plants with solid carbide cutting tools, drilling components, end mills, indexable inserts, and tool holders.
            </p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {['Solid Carbide End Mills', 'High Precision Indexable Inserts', 'Threading and Tapping Drills', 'Heavy Duty Machining Tool Holders'].map((item, i) => (
                <div key={i} className="flex items-center gap-2 font-semibold text-heading">
                  <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-premium relative overflow-hidden group hover:border-primary-200 transition-all duration-300">
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-accent/8 rounded-full blur-2xl" />
            <h3 className="font-display font-bold text-xs text-accent uppercase tracking-widest mb-5 relative z-10">Tooling Partner Specs</h3>
            <p className="text-xs text-white/60 leading-relaxed mb-6 relative z-10">
              We hold massive inventory reserves in our regional warehouse to support continuous tooling supply contracts for auto ancillary plants.
            </p>
            <div className="space-y-4 text-xs relative z-10">
              {[
                { k: 'Inventory Supply SLA', v: '< 24 Hrs Delivery' },
                { k: 'Applications', v: 'Milling, Boring, Drilling' },
              ].map((row, i) => (
                <div key={i} className={`flex justify-between py-3 ${i < 1 ? 'border-b border-white/10' : ''}`}>
                  <span className="text-white/60 font-medium">{row.k}</span>
                  <span className="font-bold text-white">{row.v}</span>
                </div>
              ))}
            </div>
            <Link to="/contact#enquiry" className="btn-accent w-full justify-center mt-8 py-3 rounded-xl text-xs relative z-10">
              Contact Tooling Representative
              <HiArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
